import React, { useEffect, useState } from "react";
import styles from "./AeternaInvicta.module.scss";

const STYLE_LABELS = { timmy: "Timmy", johnny: "Johnny", spike: "Spike" };
const SKILL_FILTERS = [
  { id: "all", label: "All Skills" },
  { id: "weak", label: "Weak" },
  { id: "medium", label: "Medium" },
  { id: "strong", label: "Strong" },
];
const RESULT_LABELS = { victory: "Win", defeat: "Loss", draw: "Draw" };

function formatWhen(value) {
  const ms = Number(value) || 0;
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

function formatRelative(value, nowMs) {
  const ms = Number(value) || 0;
  if (!ms) return "—";
  const diff = ms - nowMs;
  const minutes = Math.round(Math.abs(diff) / 60000);
  if (minutes < 1) return diff >= 0 ? "now" : "just now";
  const text = minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
  return diff >= 0 ? `in ${text}` : `${text} ago`;
}

function formatRecord(record) {
  if (!record) return "—";
  const base = `${record.wins || 0}-${record.losses || 0}-${record.draws || 0}`;
  return record.winRate == null ? base : `${base} (${record.winRate}%)`;
}

function formatAttributes(map) {
  const entries = Object.entries(map || {}).filter(([, amount]) => amount);
  if (!entries.length) return "—";
  return entries.map(([attribute, amount]) => `${attribute} ${amount}`).join(", ");
}

function formatCardList(cards) {
  return (cards || [])
    .map((card) => (card.count > 1 ? `${card.name} ×${card.count}` : card.name))
    .join(", ");
}

function StyleFit({ fit }) {
  return (
    <span>
      {Object.keys(STYLE_LABELS)
        .map((style) => `${STYLE_LABELS[style]} ${fit?.[style] ?? 0}%`)
        .join(" · ")}
    </span>
  );
}

export default function AdminPlayerBots({ send, bots, enabled, limits, analysis }) {
  const [selected, setSelected] = useState("");
  const [skillFilter, setSkillFilter] = useState("all");
  const [resetConfirm, setResetConfirm] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    setResetConfirm(false);
  }, [selected]);

  const list = Array.isArray(bots) ? bots : [];
  const visible = list.filter((bot) => skillFilter === "all" || bot.skill === skillFilter);
  const selectedBot = list.find((bot) => bot.username === selected) || null;
  const selectedAnalysis = analysis && analysis.username === selected ? analysis : null;
  const activeCount = list.filter((bot) => bot.status.startsWith("In ")).length;

  const analyze = (username) => {
    setSelected(username);
    send({ type: "ai:adminAnalyzePlayerBot", targetUsername: username });
  };

  return (
    <>
      <div className={styles.detailBlock}>
        <div className={styles.detailLine}>Player Bots</div>
        <div className={styles.muted}>
          Persistent Timmy, Johnny, and Spike accounts that queue, earn unlocks, and tune
          their own decks. Refreshes every 15 seconds while this view is open.
        </div>
        {!enabled ? (
          <div className={styles.muted}>
            The bot scheduler is disabled on this server (AI_PLAYER_BOTS=off). Bots will
            not queue until it is enabled.
          </div>
        ) : null}
        <div className={styles.row}>
          <button
            type="button"
            className={styles.button}
            onClick={() => send({ type: "ai:adminListPlayerBots" })}
          >
            Refresh Bots
          </button>
          <select
            className={styles.adminSelect}
            value={skillFilter}
            onChange={(event) => setSkillFilter(event.target.value)}
          >
            {SKILL_FILTERS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <span className={styles.muted}>
            {list.length} bots · {activeCount} active
            {limits
              ? ` · cap ${limits.cap} (${limits.ceiling} minus ${limits.humansOnline} human${
                  limits.humansOnline === 1 ? "" : "s"
                } online)`
              : ""}
          </span>
        </div>
        {!list.length ? (
          <div className={styles.muted}>No player bots have been created yet.</div>
        ) : (
          <div className={styles.adminPlayerTableWrap}>
            <table className={styles.adminPlayerTable}>
              <thead>
                <tr>
                  <th>Bot</th>
                  <th>Profile</th>
                  <th>Status</th>
                  <th>Games</th>
                  <th>Record</th>
                  <th>Last 10</th>
                  <th>Collection</th>
                  <th>Active Deck</th>
                  <th>Next Deck Change</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((bot) => (
                  <tr
                    key={bot.username}
                    className={selected === bot.username ? styles.adminPlayerRowSelected : undefined}
                    onClick={() => analyze(bot.username)}
                  >
                    <td>
                      <div className={styles.adminPlayerName}>{bot.username}</div>
                      <div className={styles.muted}>CPU {bot.difficulty}</div>
                    </td>
                    <td>{bot.label}</td>
                    <td>
                      {bot.paused ? "Paused" : bot.status}
                      {!bot.paused && bot.nextActivityAt > nowMs && !bot.status.startsWith("In ") ? (
                        <div className={styles.muted}>
                          {formatRelative(bot.nextActivityAt, nowMs)}
                        </div>
                      ) : null}
                    </td>
                    <td>{bot.gamesPlayed}</td>
                    <td>
                      {bot.record
                        ? `${bot.record.wins || 0}-${bot.record.losses || 0}-${bot.record.draws || 0}`
                        : "—"}
                    </td>
                    <td>{bot.form || "—"}</td>
                    <td>
                      {bot.collection.owned}/{bot.collection.total}
                    </td>
                    <td>
                      {bot.activeDeck ? (
                        <>
                          <div>{bot.activeDeck.name}</div>
                          <div className={styles.muted}>
                            {bot.activeDeck.aspectName} · {bot.deckCount} deck
                            {bot.deckCount === 1 ? "" : "s"}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {bot.gamesUntilDeckChange} game
                      {bot.gamesUntilDeckChange === 1 ? "" : "s"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedBot ? (
        <div className={styles.detailBlock}>
          <div className={styles.detailLine}>
            {selectedBot.username} · {selectedBot.label}
          </div>
          <div className={styles.row}>
            <button type="button" className={styles.button} onClick={() => analyze(selectedBot.username)}>
              Analyze
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() =>
                send({
                  type: "ai:adminSetPlayerBotPaused",
                  targetUsername: selectedBot.username,
                  paused: !selectedBot.paused,
                })
              }
            >
              {selectedBot.paused ? "Resume" : "Pause"}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() =>
                send({ type: "ai:adminRebuildPlayerBotDecks", targetUsername: selectedBot.username })
              }
            >
              Rebuild Decks
            </button>
            {resetConfirm ? (
              <>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonDanger}`}
                  onClick={() => {
                    setResetConfirm(false);
                    send({ type: "ai:adminResetPlayerBot", targetUsername: selectedBot.username });
                  }}
                >
                  Confirm Reset
                </button>
                <button type="button" className={styles.button} onClick={() => setResetConfirm(false)}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonDanger}`}
                onClick={() => setResetConfirm(true)}
              >
                Reset Progress
              </button>
            )}
          </div>
          {resetConfirm ? (
            <div className={styles.muted}>
              Reset wipes this bot&apos;s collection, records, currency, and history, then
              rebuilds a starter deck that fits its personality.
            </div>
          ) : null}

          {!selectedAnalysis ? (
            <div className={styles.muted}>Loading analysis…</div>
          ) : (
            <>
              <div className={styles.detailLine}>
                Personality:{" "}
                {Object.entries(selectedAnalysis.weights || {})
                  .map(([style, weight]) => `${STYLE_LABELS[style] || style} ${Math.round(weight * 100)}%`)
                  .join(" / ")}{" "}
                · Plays CPU logic at {selectedAnalysis.difficulty} difficulty
              </div>
              <div className={styles.detailLine}>
                Overall {formatRecord(selectedAnalysis.overall)} · vs Humans{" "}
                {formatRecord(selectedAnalysis.vsHuman)} · vs Bots {formatRecord(selectedAnalysis.vsBot)} · vs
                CPU {formatRecord(selectedAnalysis.vsCpu)}
              </div>
              <div className={styles.detailLine}>
                Currency {selectedAnalysis.currency} · Marketplace buys {selectedAnalysis.purchases} · Created{" "}
                {formatWhen(selectedAnalysis.createdAt)} · Last active{" "}
                {formatRelative(selectedAnalysis.lastActiveAt, nowMs)}
              </div>
              <div className={styles.detailLine}>
                Collection {selectedAnalysis.collection.owned}/{selectedAnalysis.collection.total}:{" "}
                {selectedAnalysis.collection.bySet
                  .map((entry) => `${entry.set} ${entry.owned}/${entry.total}`)
                  .join(" · ")}
              </div>

              <div className={styles.detailLine}>Decks</div>
              <div className={styles.adminPlayerTableWrap}>
                <table className={styles.adminPlayerTable}>
                  <thead>
                    <tr>
                      <th>Deck</th>
                      <th>Record</th>
                      <th>Power</th>
                      <th>Style Fit</th>
                      <th>Profile Fit</th>
                      <th>Quality</th>
                      <th>Slots</th>
                      <th>Supply / Demand</th>
                      <th>Dead Cards</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAnalysis.decks.map((deck) => (
                      <tr key={deck.id} className={deck.active ? styles.adminPlayerRowSelected : undefined}>
                        <td>
                          <div className={styles.adminPlayerName}>
                            {deck.name}
                            {deck.active ? " (Active)" : ""}
                          </div>
                          <div className={styles.muted}>{deck.aspectName}</div>
                        </td>
                        <td>
                          {deck.stats.wins}-{deck.stats.losses}-{deck.stats.draws}
                        </td>
                        <td>
                          {deck.power}/{deck.maxPower}
                        </td>
                        <td>
                          <StyleFit fit={deck.styleFit} />
                        </td>
                        <td>{deck.profileFit}%</td>
                        <td>
                          {deck.quality}
                          {deck.consistencyPenalty ? (
                            <div className={styles.muted}>−{deck.consistencyPenalty} consistency</div>
                          ) : null}
                        </td>
                        <td>
                          W{deck.slots?.weapon ?? 0} · A{deck.slots?.armor ?? 0} · T{deck.slots?.trinket ?? 0}
                          <div className={styles.muted}>
                            {deck.resourceProducers} producers · {deck.activeAbilities} actives ·{" "}
                            {deck.modifierAbilities} modifiers
                          </div>
                        </td>
                        <td>
                          <div>Supply: {formatAttributes(deck.supply)}</div>
                          <div className={styles.muted}>
                            Demand: {formatAttributes(deck.demand)} · avg cost {deck.averageAbilityCost}
                          </div>
                        </td>
                        <td>{deck.deadCards.length ? deck.deadCards.join(", ") : "None"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedAnalysis.decks
                .filter((deck) => deck.active)
                .map((deck) => (
                  <div key={deck.id} className={styles.detailBlock}>
                    <div className={styles.detailLine}>Equipment: {formatCardList(deck.equipment)}</div>
                    <div className={styles.detailLine}>Abilities: {formatCardList(deck.abilities)}</div>
                    <div className={styles.muted}>Damage potential {deck.damagePotential}</div>
                  </div>
                ))}

              <div className={styles.detailLine}>Recent Games</div>
              {!selectedAnalysis.history.length ? (
                <div className={styles.muted}>No games played yet.</div>
              ) : (
                <div className={styles.adminPlayerTableWrap}>
                  <table className={styles.adminPlayerTable}>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Result</th>
                        <th>Opponent</th>
                        <th>Ended By</th>
                        <th>Rounds</th>
                        <th>Deck</th>
                        <th>Play Style</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAnalysis.history.map((entry, index) => (
                        <tr key={`${entry.at}-${index}`}>
                          <td>{formatWhen(entry.at)}</td>
                          <td>{RESULT_LABELS[entry.result] || entry.result}</td>
                          <td>
                            {entry.opponent || "—"}
                            <div className={styles.muted}>{entry.opponentKind}</div>
                          </td>
                          <td>{entry.endedBy || "—"}</td>
                          <td>{entry.rounds || "—"}</td>
                          <td>{entry.deckName || "—"}</td>
                          <td>{entry.personality || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className={styles.detailLine}>Deck Changes</div>
              {!selectedAnalysis.deckChanges.length ? (
                <div className={styles.muted}>No deck changes yet.</div>
              ) : (
                <div className={styles.adminPlayerTableWrap}>
                  <table className={styles.adminPlayerTable}>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Change</th>
                        <th>Deck</th>
                        <th>Detail</th>
                        <th>Cards Owned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAnalysis.deckChanges.map((entry, index) => (
                        <tr key={`${entry.at}-${index}`}>
                          <td>{formatWhen(entry.at)}</td>
                          <td>{entry.mode}</td>
                          <td>{entry.deckName || "—"}</td>
                          <td>{entry.detail}</td>
                          <td>{entry.ownedCount || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className={styles.muted}>Select a bot to analyze it.</div>
      )}
    </>
  );
}
